import { useTranslation } from 'react-i18next';
import {
  ALF_MIN_CITED_OPTIONS,
  ALF_PERIOD_PRESET_DAYS,
  NEEDS_REVIEW_RATE_THRESHOLD,
  alfRateTone,
  channelTalkStatisticsUrl,
} from '../../lib/alf-performance.js';
import type { AlfOverview, AlfPeriodPreset, AlfRateTone } from '../../lib/alf-performance.js';
import type { FaqAlfPerformanceItem, FaqAlfPerformanceResponse } from '../../types/faq-monitor.js';
import { DISPOSITION_OPTIONS, DispositionControl } from './DispositionControl.js';
import { SeverityCountChip } from './SeverityBadges.js';
import { QuoteToggle, SourceLink } from './WorklistItems.js';

/**
 * 完結率のバーの色。
 *
 * 低い＝赤にはしない。低い理由には「記事が足りない」と「そもそも人が対応すべき」の
 * 2つがあり、後者は正常動作。赤で塗ると中身を読む前に「不良」と判断させてしまう。
 * @graph-connects none
 */
const RATE_TONE_CLS: Record<AlfRateTone, string> = {
  low: 'bg-amber-500',
  mid: 'bg-blue-500',
  high: 'bg-emerald-500',
};

/**
 * 数字カード1枚。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business ALF実績の要約数値を、見出し・値・補足の3段で1枚ずつ表示する
 * @graph-connects none
 */
export function AlfOverviewCard({
  label,
  value,
  sub,
  emphasized = false,
}: {
  label: string;
  value: string;
  sub: string;
  emphasized?: boolean;
}) {
  return (
    <div
      className={`min-w-0 flex-1 rounded-lg border px-3 py-2 ${
        emphasized ? 'border-amber-500/20 bg-amber-500/10' : 'border-border bg-background-secondary'
      }`}
    >
      <p className="truncate text-xs text-foreground-muted">{label}</p>
      <p className="mt-0.5 text-2xl leading-none font-bold tabular-nums text-foreground">{value}</p>
      <p className="mt-1 text-xs text-foreground-secondary">{sub}</p>
    </div>
  );
}

/**
 * 要約の数字カード。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 引用された記事数・問い合わせ数・完結率・見直したい記事数を並べて全体像を示す
 * @graph-connects AlfOverviewCard [calls] 数字カード1枚の表示
 */
export function AlfOverviewCards({
  overview,
  minCited,
}: {
  overview: AlfOverview;
  minCited: number;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap gap-3">
      <AlfOverviewCard
        label={t('faqMonitor.alfCardTitles')}
        value={String(overview.matchedTitles)}
        sub={t('faqMonitor.alfCardTitlesSub', { listed: overview.listedTitles, minCited })}
      />
      <AlfOverviewCard
        label={t('faqMonitor.alfCardCited')}
        value={String(overview.totalCitedChats)}
        sub={t('faqMonitor.alfCardCitedSub', { listed: overview.listedCitedChats })}
      />
      <AlfOverviewCard
        label={t('faqMonitor.alfCardRate')}
        value={overview.listedResolveRate === null ? '—' : `${String(overview.listedResolveRate)}%`}
        sub={t('faqMonitor.alfCardRateSub', {
          resolved: overview.listedResolvedChats,
          cited: overview.listedCitedChats,
        })}
      />
      <AlfOverviewCard
        label={t('faqMonitor.alfCardNeedsReview')}
        value={String(overview.needsReview)}
        sub={t('faqMonitor.alfCardNeedsReviewSub', { threshold: NEEDS_REVIEW_RATE_THRESHOLD })}
        emphasized
      />
    </div>
  );
}

/**
 * 期間と引用回数の下限を選ぶ操作列。
 *
 * 並び順は選ばせない。完結率の低い順に固定して、直す価値のありそうな記事から
 * 目に入るようにする。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 集計期間と引用回数の下限を選び、同じ期間の公式統計へ移動できるようにする
 * @graph-connects channelTalkStatisticsUrl [calls] 公式統計画面のURL組み立て
 */
export function AlfControls({
  preset,
  onPresetChange,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  minCited,
  onMinCitedChange,
  periodFrom,
  periodTo,
}: {
  preset: AlfPeriodPreset;
  onPresetChange: (preset: AlfPeriodPreset) => void;
  customFrom: string;
  customTo: string;
  onCustomFromChange: (value: string) => void;
  onCustomToChange: (value: string) => void;
  minCited: number;
  onMinCitedChange: (minCited: number) => void;
  periodFrom: string;
  periodTo: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
      <label className="flex items-center gap-2">
        <span className="text-foreground-muted">{t('faqMonitor.alfPeriodLabel')}</span>
        <select
          value={String(preset)}
          onChange={(e) => {
            onPresetChange(
              e.target.value === 'custom'
                ? 'custom'
                : (Number(e.target.value) as (typeof ALF_PERIOD_PRESET_DAYS)[number]),
            );
          }}
          className="rounded border border-border bg-background px-2 py-1 text-foreground"
        >
          {ALF_PERIOD_PRESET_DAYS.map((days) => (
            <option key={days} value={days}>
              {t('faqMonitor.alfPeriodDays', { days })}
            </option>
          ))}
          <option value="custom">{t('faqMonitor.alfPeriodCustom')}</option>
        </select>
      </label>

      {preset === 'custom' ? (
        <span className="flex items-center gap-1">
          <input
            type="date"
            value={customFrom}
            aria-label={t('faqMonitor.alfPeriodFrom')}
            onChange={(e) => {
              onCustomFromChange(e.target.value);
            }}
            className="rounded border border-border bg-background px-2 py-1 text-foreground"
          />
          <span className="text-foreground-muted">–</span>
          <input
            type="date"
            value={customTo}
            aria-label={t('faqMonitor.alfPeriodTo')}
            onChange={(e) => {
              onCustomToChange(e.target.value);
            }}
            className="rounded border border-border bg-background px-2 py-1 text-foreground"
          />
        </span>
      ) : (
        <span className="tabular-nums text-foreground-secondary">
          {t('faqMonitor.alfPeriodRange', { from: periodFrom, to: periodTo })}
        </span>
      )}

      <label className="flex items-center gap-2">
        <span className="text-foreground-muted">{t('faqMonitor.alfMinCitedLabel')}</span>
        <select
          value={minCited}
          onChange={(e) => {
            onMinCitedChange(Number(e.target.value));
          }}
          className="rounded border border-border bg-background px-2 py-1 text-foreground"
        >
          {ALF_MIN_CITED_OPTIONS.map((count) => (
            <option key={count} value={count}>
              {t('faqMonitor.alfMinCitedOption', { count })}
            </option>
          ))}
        </select>
      </label>

      <span className="text-foreground-muted">{t('faqMonitor.alfSortNote')}</span>

      <a
        href={channelTalkStatisticsUrl(periodFrom, periodTo)}
        target="_blank"
        rel="noreferrer"
        className="ml-auto text-blue-600 underline dark:text-blue-400"
      >
        {t('faqMonitor.alfOpenOfficial')}
      </a>
    </div>
  );
}

/**
 * 完結率のバーと数値。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 記事ごとの完結率を、長さと色で比べられるバーとして表示する
 * @graph-connects alfRateTone [calls] 完結率の帯の判定
 */
export function AlfRateBar({ rate }: { rate: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-border">
        <span
          className={`block h-full ${RATE_TONE_CLS[alfRateTone(rate)]}`}
          style={{ width: `${String(Math.min(rate, 100))}%` }}
        />
      </span>
      <span className="tabular-nums text-foreground">{rate}%</span>
    </div>
  );
}

/**
 * 記事1本ぶんの行と、開いたときの引用元の問い合わせ。
 *
 * 数字だけを見て「この記事が悪い」と決めさせないため、引用元の問い合わせを
 * 同じ行から開けるようにする。実データでは、完結率が最下位の記事の中身が
 * 二重請求や破損の相談で、ALF が正しく人へ繋いだ結果だったことがある。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 記事ごとの引用数・完結数・完結率・既知の指摘・対応方針を1行で示し、引用元の問い合わせを開けるようにする
 * @graph-connects AlfRateBar [calls] 完結率の表示
 * @graph-connects SeverityCountChip [calls] 既知の指摘の重大度と件数
 * @graph-connects DispositionControl [calls] 対応方針の表示と編集
 * @graph-connects QuoteToggle [calls] 引用元の問い合わせの開閉
 * @graph-connects SourceLink [calls] FAQ記事へのリンク
 */
export function AlfPerformanceRow({
  item,
  open,
  onToggle,
}: {
  item: FaqAlfPerformanceItem;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const statusLabelKey = DISPOSITION_OPTIONS.find(
    (o) => o.value === item.disposition.status,
  )?.labelKey;

  return (
    <>
      <tr className="border-t border-border-subtle align-top">
        <td className="px-2 py-2">
          <p className="font-medium text-foreground">{item.articleTitle}</p>
          <p className="mt-0.5 text-xs text-foreground-muted">
            {item.articleId ?? t('faqMonitor.alfUnmatchedArticle')}
          </p>
        </td>
        <td className="px-2 py-2 text-right tabular-nums text-foreground">{item.citedChats}</td>
        <td className="px-2 py-2 text-right tabular-nums text-foreground">
          {item.alfResolvedChats}
        </td>
        <td className="px-2 py-2">
          <AlfRateBar rate={item.alfResolveRate} />
        </td>
        <td className="px-2 py-2">
          {item.diagnosticSeverity === null ? (
            <span className="text-xs text-foreground-muted">{t('faqMonitor.alfNoDiagnostic')}</span>
          ) : (
            <SeverityCountChip severity={item.diagnosticSeverity} count={item.diagnosticFindings} />
          )}
        </td>
        <td className="px-2 py-2 text-xs text-foreground-secondary">
          {statusLabelKey === undefined ? item.disposition.status : t(statusLabelKey)}
        </td>
        <td className="px-2 py-2 text-right">
          <QuoteToggle open={open} onToggle={onToggle} />
        </td>
      </tr>
      {open && (
        <tr className="border-t border-border-subtle bg-background-secondary">
          <td colSpan={7} className="px-3 py-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <SourceLink url={item.articleUrl} labelKey="faqMonitor.openArticle" />
              <div className="ml-auto">
                <DispositionControl hash={item.dispositionHash} disposition={item.disposition} />
              </div>
            </div>
            <p className="mt-3 text-xs font-medium text-foreground-muted">
              {t('faqMonitor.alfEvidenceChats')}
            </p>
            <ul className="mt-1 space-y-1">
              {item.evidenceChats.length === 0 ? (
                <li className="text-sm text-foreground-muted">{t('faqMonitor.alfNoEvidence')}</li>
              ) : (
                item.evidenceChats.map((chat) => (
                  <li
                    key={chat}
                    className="border-l-2 border-border pl-3 text-sm whitespace-pre-wrap text-foreground-secondary"
                  >
                    {chat}
                  </li>
                ))
              )}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * 一覧から外れたぶんの内訳。
 *
 * 引用が下限に満たない記事と、FAQ 記事に結び付かなかった引用を件数で出す。
 * 表に出ている数字だけを見て「これが全部」と読まれないようにするため、
 * 落とした量を必ず並べて置く。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 一覧から外した記事と、FAQ記事に結び付かなかった引用の量を明示する
 * @graph-connects none
 */
export function AlfExcludedNote({
  data,
  minCited,
}: {
  data: FaqAlfPerformanceResponse;
  minCited: number;
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-md border border-border bg-background-secondary px-3 py-2 text-xs text-foreground-secondary">
      <dl className="grid gap-1">
        <div className="flex flex-wrap gap-x-2">
          <dt className="font-medium text-foreground">{t('faqMonitor.alfBelowThresholdLabel')}</dt>
          <dd className="tabular-nums">
            {t('faqMonitor.alfExcludedCount', {
              titles: data.belowThresholdTitles,
              chats: data.belowThresholdCitedChats,
              minCited,
            })}
          </dd>
        </div>
        <div className="flex flex-wrap gap-x-2">
          <dt className="font-medium text-foreground">{t('faqMonitor.alfUnmatchedLabel')}</dt>
          <dd className="tabular-nums">
            {t('faqMonitor.alfUnmatchedCount', {
              titles: data.unmatchedTitles,
              chats: data.unmatchedCitedChats,
            })}
          </dd>
        </div>
      </dl>
      <p className="mt-1">{t('faqMonitor.alfUnmatchedDesc')}</p>
    </div>
  );
}
